import { inject, Injectable } from '@angular/core';
import {
  type CreateDirectUploadInput,
  type CreateDirectUploadMutation as CreateDirectUploadMutationMutation,
  type CreateDirectUploadMutationVariables,
  type DestroyAttachedFileMutation as DestroyAttachedFileMutationMutation,
  type DestroyAttachedFileMutationVariables,
  type DirectUpload,
} from '@reconcept-platform/shared/types';
import { type UploadableFile, UploadFileStatusEnum } from '@reconcept-platform/shared/util';
import { forkJoin, type Observable, of, timer } from 'rxjs';
import { catchError, last, map, retry, switchMap } from 'rxjs/operators';
import { SharedGraphQlService } from '../../services/shared-graphql.service';
import { type FileUploadProgress, UploadService } from '../../services/upload.service';
import { invalidateObject } from '../../utils/cache-invalidator';
import { CreateDirectUploadMutation, DestroyAttachedFileMutation } from './upload.mutations';

const RETRYABLE_UPLOAD_STATUSES = [0, 403, 500, 502, 503, 504];

@Injectable({
  providedIn: 'root',
})
export class UploadApi {
  private uploadService = inject(UploadService);
  private sharedGraphQl = inject(SharedGraphQlService);

  createDirectUploadsAndUploadFiles(files: UploadableFile[], errorMessage: string): Observable<DirectUpload[]> {
    // forkJoin only fires next handler when all observables are COMPLETED
    return forkJoin(
      files
        // don't upload files if the frontend already views them as invalid
        .filter((f) => f.fileBlob && !f.hasError())
        .map((file) => this.createDirectUploadAndUploadFile(file, errorMessage)),
    ).pipe(
      // filter out failed uploads
      map((directUploads) => {
        return directUploads.filter((du) => !!du);
      }),
    );
  }

  destroyAttachedFile(id: string) {
    return this.sharedGraphQl.mutateV2<DestroyAttachedFileMutationMutation, DestroyAttachedFileMutationVariables>({
      mutation: DestroyAttachedFileMutation,
      variables: {
        input: {
          attachedFileId: id,
        },
      },
      update: invalidateObject('AttachedFile', id),
    });
  }

  // destroy multiple attachedfiles
  destroyAttachedFiles(ids: string[]): Observable<any> {
    // check if there are uploads to be removed
    if (!ids || !ids.length) {
      return of(null);
    } else {
      return forkJoin(ids.map((id) => this.destroyAttachedFile(id)));
    }
  }

  // creates a DirectUpload
  private createDirectUpload(directUploadInput: CreateDirectUploadInput): Observable<DirectUpload> {
    // @ts-expect-error TS2322 type not assignable
    return this.sharedGraphQl
      .mutateV2<CreateDirectUploadMutationMutation, CreateDirectUploadMutationVariables>({
        mutation: CreateDirectUploadMutation,
        variables: {
          input: directUploadInput,
        },
      })
      .pipe(
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        map((result) => result.data.createDirectUpload.directUpload),
      );
  }

  private createDirectUploadAndUploadFile(file: UploadableFile, errorMessage: string) {
    let directUpload: DirectUpload;

    return this.uploadService.getFileChecksum(file.fileBlob).pipe(
      // the checksum is calculated once, every retry creates a new DU so it never reuses an expired presigned url
      switchMap((checksum) =>
        this.createDirectUpload({
          filename: file.fileBlob.name,
          byteSize: file.fileBlob.size,
          contentType: file.fileBlob.type,
          checksum,
        }).pipe(
          switchMap((du) => {
            directUpload = du;
            file.directUploadId = du.signedId ?? undefined;
            return this.uploadService.uploadFile(directUpload, file.fileBlob);
          }),
          retry({
            count: 5,
            delay: (error: FileUploadProgress | Error, retryCount) => {
              // 0 = no response, 403 = presigned url expired, 5xx = upload service or gateway failed;
              // a 422 (virus detected, plain Error) or 400 (bad request) will never succeed on retry
              if (error && 'status' in error && RETRYABLE_UPLOAD_STATUSES.includes(error.status)) {
                // wait a few seconds before retrying
                return timer(retryCount * 1000);
              }
              throw error;
            },
          }),
        ),
      ),
      map((uploadProgress) => {
        // update the progress
        file.setUploadProgress(uploadProgress.bytesPercentage);

        // set status uploaded, the upload is done
        if (uploadProgress.type === 'load') {
          file.setStatus(UploadFileStatusEnum.uploaded);
        }

        return directUpload;
      }),
      last(),
      catchError((err) => {
        // the blob never reached S3, so consumers must not attach it
        file.directUploadId = undefined;

        // we catch an error and warn user to try again
        if (err instanceof Error) {
          file.setError(`${errorMessage}: ${err.message} `);
        } else {
          file.setError(errorMessage);
        }

        // don't include as successful result
        return of(null);
      }),
    );
  }
}
